import { api } from '@ui/app/api';
import type {
    CreateReportingSourceDto,
    CreateProjectDto,
    CaseExtractionStatus,
    PatientRegistryReportingSourceListParams,
    PatientRegistryReportingSourceListResponse,
    PatientRegistryReportingSource,
    PatientRegistryProjectCategoriesResponse,
    PatientRegistryProjectsListParams,
    PatientRegistryProjectsListResponse,
    PatientRegistryProject,
    PatientRegistrySelectionSummaryRequest,
    PatientRegistrySelectionSummaryResponse,
    ProjectTitleAvailabilityResponse,
    UpdateProjectDto,
} from '../types';
import type {
    ProjectCasesListResponse,
    CaseReviewResponse,
    MarkNotReportableRequest,
    SubmitCaseRequest,
    RequestChangesRequest,
    ApproveCaseRequest,
} from '../types/case-status';
import type {
    CurationVersion,
    CurationVersionListResponse,
    CurationVersionWithEdits,
} from '../../CurationResults/types/version';
import type { SaveVersionRequest } from '../../CurationResults/types/version';

type CaseVersionsCompareResponse = {
    from: { id: string; version_number: number; version_name: string | null };
    to: { id: string; version_number: number; version_name: string | null };
    summary: {
        field_change_count: number;
        evidence_added_count: number;
        evidence_removed_count: number;
    };
    field_changes: Array<{
        field_path: string;
        field_name: string;
        category: string;
        from_value: any;
        to_value: any;
    }>;
    evidence_changes: {
        added: any[];
        removed: any[];
    };
};

type ValidateCaseFieldsResponse = {
    valid: boolean;
    errorCount: number;
    warningCount: number;
    messageCount: number;
    metafileTag: string;
    editsetTag: string;
    errors: Array<{
        editName: string;
        message: string;
        msgType: 'E' | 'W' | 'M';
        errorFields: Array<{
            fieldNumber: number;
            fieldName: string;
        }>;
    }>;
};

export const projectsApi = api.injectEndpoints({
    endpoints: builder => ({
        // projects
        getProjects: builder.query<PatientRegistryProjectsListResponse, PatientRegistryProjectsListParams | void>({
            query: params => ({
                url: '/patient-registry/projects',
                params: {
                    page: params?.page ?? 1,
                    limit: params?.limit ?? 10,
                    search: params?.search || undefined,
                    category: params?.category || undefined,
                    state: params?.state || undefined,
                },
            }),
            providesTags: ['PatientRegistryProjects'],
        }),

        getProjectCategories: builder.query<PatientRegistryProjectCategoriesResponse, void>({
            query: () => ({
                url: '/patient-registry/projects/categories',
            }),
        }),
        getProject: builder.query<PatientRegistryProject, string>({
            query: id => `/patient-registry/projects/${id}`,
            providesTags: (result, error, id) => [{ type: 'PatientRegistryProjects', id }],
        }),

        getProjectFieldDefinitions: builder.query<
            {
                fields: Array<{
                    id: string;
                    name: string;
                    displayName: string;
                    description: string;
                    dataType: string;
                    extractionInstruction: string;
                    required: boolean;
                    category: string;
                    itemNumber: string;
                    allowedValues?: string[];
                    validValues?: Array<{ code: string; description: string }>;
                }>;
            },
            { projectId: string; caseId: string }
        >({
            query: ({ projectId, caseId }) =>
                `/patient-registry/projects/${projectId}/cases/${caseId}/field-definitions`,
        }),

        getCaseExtractionResult: builder.query<unknown, { projectId: string; patientId: string; versionId?: string }>({
            query: ({ projectId, patientId, versionId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${patientId}/extraction-result`,
                params: {
                    versionId: versionId || undefined,
                },
            }),
        }),

        getCaseStatus: builder.query<CaseExtractionStatus, { projectId: string; caseId: string }>({
            query: ({ projectId, caseId }) => `/patient-registry/projects/${projectId}/cases/${caseId}/status`,
        }),

        getCaseDocuments: builder.query<
            {
                documents: Array<{
                    id: string;
                    name: string;
                    date: string;
                    documentType: string;
                    sourceValue?: string;
                }>;
                documentRelevancyScores: Record<string, number>;
                total: number;
                page: number;
                pageSize: number;
                totalPages: number;
            },
            { projectId: string; patientId: string; page?: number; pageSize?: number }
        >({
            query: ({ projectId, patientId, page = 1, pageSize = 100 }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${patientId}/documents`,
                params: { page, pageSize },
            }),
        }),

        getCaseAuditTrail: builder.query<
            {
                logs: Array<{
                    id: string;
                    created_at: string;
                    user_name: string | null;
                    user_email: string | null;
                    user_id: string;
                    action: string;
                    description: string | null;
                    status: string;
                    error_message: string | null;
                    workflow: {
                        action?: string;
                        role?: string;
                        fromStatus?: string;
                        toStatus?: string;
                        caseId?: string;
                        projectId?: string;
                        submittedVersion?: { id: string; versionNumber: number; versionName: string | null } | null;
                        feedback?: string;
                        reason?: string;
                        notes?: string;
                    } | null;
                    caseFindingSummary: {
                        totalPatients: number;
                        totalDocuments: number;
                        casesFound: number;
                        cases: Array<{
                            personId: number;
                            confirmedCancers: string[];
                            totalDocuments: number;
                            aboveThreshold: number;
                            cancerDetails: Array<{
                                cancerType: string;
                                documentCount: number;
                                dateWindow: { earliest_date?: string; latest_date?: string } | null;
                            }>;
                        }>;
                    } | null;
                    extractionSummary: {
                        totalFieldsExtracted: number;
                        cancersFound: number;
                        sourceDocumentsUsed: number;
                        processingTimeSeconds: number | null;
                        cancerSummaries: Array<{
                            primarySite: string;
                            histology: string;
                            diagnosisDate: string | null;
                            fieldsExtracted: number;
                            confidenceLevel: string | null;
                            registryReady: boolean;
                        }>;
                    } | null;
                }>;
                total: number;
            },
            { projectId: string; patientId: string }
        >({
            query: ({ projectId, patientId }) =>
                `/patient-registry/projects/${projectId}/cases/${patientId}/audit-trail`,
            providesTags: (_result, _error, { projectId, patientId }) => [
                { type: 'Audit', id: `case-trail-${projectId}-${patientId}` },
            ],
        }),

        getCaseVersions: builder.query<CurationVersionListResponse, { projectId: string; patientId: string }>({
            query: ({ projectId, patientId }) => `/patient-registry/projects/${projectId}/cases/${patientId}/versions`,
            providesTags: ['CurationVersions'],
        }),

        compareCaseVersions: builder.query<
            CaseVersionsCompareResponse,
            { projectId: string; patientId: string; fromVersionId: string; toVersionId: string }
        >({
            query: ({ projectId, patientId, fromVersionId, toVersionId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${patientId}/versions/compare`,
                params: {
                    fromVersionId,
                    toVersionId,
                },
            }),
        }),

        getCaseVersion: builder.query<
            CurationVersionWithEdits,
            { projectId: string; patientId: string; versionId: string }
        >({
            query: ({ projectId, patientId, versionId }) =>
                `/patient-registry/projects/${projectId}/cases/${patientId}/versions/${versionId}`,
        }),

        activateCaseVersion: builder.mutation<
            CurationVersion,
            { projectId: string; patientId: string; versionId: string }
        >({
            query: ({ projectId, patientId, versionId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${patientId}/versions/${versionId}/activate`,
                method: 'PUT',
            }),
            invalidatesTags: ['CurationVersions'],
        }),

        resetCaseVersions: builder.mutation<{ success: boolean }, { projectId: string; patientId: string }>({
            query: ({ projectId, patientId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${patientId}/versions/reset-to-original`,
                method: 'PUT',
            }),
            invalidatesTags: ['CurationVersions'],
        }),

        saveCaseVersion: builder.mutation<
            CurationVersionWithEdits,
            { projectId: string; patientId: string; data: SaveVersionRequest }
        >({
            query: ({ projectId, patientId, data }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${patientId}/versions`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['CurationVersions'],
        }),

        getProjectCases: builder.query<
            ProjectCasesListResponse,
            { projectId: string; page?: number; pageSize?: number; search?: string }
        >({
            query: ({ projectId, page, pageSize, search }) => ({
                url: `/patient-registry/projects/${projectId}/cases`,
                params: {
                    page: page ?? 1,
                    pageSize: pageSize ?? 50,
                    search: search?.trim() ? search.trim() : undefined,
                },
            }),
        }),

        checkProjectTitleAvailability: builder.query<
            ProjectTitleAvailabilityResponse,
            { title: string; excludeId?: string }
        >({
            query: ({ title, excludeId }) => ({
                url: '/patient-registry/projects/title-availability',
                params: {
                    title,
                    excludeId,
                },
            }),
        }),
        createProject: builder.mutation<PatientRegistryProject, CreateProjectDto>({
            query: body => ({
                url: '/patient-registry/projects',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PatientRegistryProjects'],
        }),
        updateProject: builder.mutation<PatientRegistryProject, { id: string; data: UpdateProjectDto }>({
            query: ({ id, data }) => ({
                url: `/patient-registry/projects/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                'PatientRegistryProjects',
                { type: 'PatientRegistryProjects', id },
            ],
        }),
        deleteProject: builder.mutation<void, string>({
            query: id => ({
                url: `/patient-registry/projects/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PatientRegistryProjects'],
        }),

        // Case Finding
        getCaseFindingStatus: builder.query<{ projectId: string; status: string; casesCount: number }, string>({
            query: projectId => `/patient-registry/projects/${projectId}/case-finding-status`,
        }),

        rerunCaseFinding: builder.mutation<void, string>({
            query: projectId => ({
                url: `/patient-registry/projects/${projectId}/rerun-case-finding`,
                method: 'POST',
            }),
        }),

        // Per-case extraction
        startCaseExtraction: builder.mutation<
            {
                projectId: string;
                caseId: string;
                curationJobId: string;
                datasetId: string;
                documentsCount: number;
            },
            { projectId: string; caseId: string; documentIds?: number[] }
        >({
            query: ({ projectId, caseId, documentIds }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/start-extraction`,
                method: 'POST',
                body: { documentIds },
            }),
        }),

        // Reporting Sources
        getReportingSources: builder.query<
            PatientRegistryReportingSourceListResponse,
            PatientRegistryReportingSourceListParams | void
        >({
            query: params => ({
                url: '/patient-registry/reporting-source',
                params: {
                    page: params?.page ?? 1,
                    limit: params?.limit ?? 10,
                    search: params?.search || undefined,
                },
            }),
            providesTags: ['PatientRegistryReportingSources'],
        }),

        createReportingSource: builder.mutation<PatientRegistryReportingSource, CreateReportingSourceDto>({
            query: body => ({
                url: '/patient-registry/reporting-source',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PatientRegistryReportingSources'],
        }),

        deleteReportingSource: builder.mutation<{ success: boolean }, string>({
            query: id => ({
                url: `/patient-registry/reporting-source/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PatientRegistryReportingSources'],
        }),

        getSelectionSummary: builder.query<
            PatientRegistrySelectionSummaryResponse,
            PatientRegistrySelectionSummaryRequest
        >({
            query: body => ({
                url: '/patient-registry/selection-summary',
                method: 'POST',
                body,
            }),
        }),

        // Case Review Endpoints
        getCaseReview: builder.query<CaseReviewResponse, { projectId: string; caseId: string }>({
            query: ({ projectId, caseId }) => `/patient-registry/projects/${projectId}/cases/${caseId}/review`,
            providesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
            ],
        }),

        getProjectCaseReviews: builder.query<CaseReviewResponse[], string>({
            query: projectId => `/patient-registry/projects/${projectId}/cases-reviews`,
            providesTags: (result, error, projectId) => [{ type: 'CaseReview' as const, id: projectId }],
        }),

        claimCase: builder.mutation<CaseReviewResponse, { projectId: string; caseId: string }>({
            query: ({ projectId, caseId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/claim`,
                method: 'POST',
            }),
            async onQueryStarted({ projectId, caseId }, { dispatch, queryFulfilled, getState }) {
                const now = new Date().toISOString();
                const state: any = getState();
                const currentUserId: string | undefined = state?.user?.id ?? undefined;
                const currentUserName: string | undefined = state?.user?.name ?? undefined;

                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'in-progress';
                            (draft as any).claimedBy = currentUserId ?? (draft as any).claimedBy;
                            (draft as any).claimedByName = currentUserName ?? (draft as any).claimedByName;
                            (draft as any).claimedAt = now;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'in-progress';
                            (draft as any).claimedBy = currentUserId ?? (draft as any).claimedBy;
                            (draft as any).claimedByName = currentUserName ?? (draft as any).claimedByName;
                            (draft as any).claimedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = 'in-progress';
                                (draft[idx] as any).claimedBy = currentUserId ?? (draft[idx] as any).claimedBy;
                                (draft[idx] as any).claimedByName =
                                    currentUserName ?? (draft[idx] as any).claimedByName;
                                (draft[idx] as any).claimedAt = now;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                            (draft as any).claimedBy = data.claimedBy;
                            (draft as any).claimedByName = data.claimedByName;
                            (draft as any).claimedAt = data.claimedAt;
                            (draft as any).reviewedBy = data.reviewedBy;
                            (draft as any).reviewedByName = data.reviewedByName;
                            (draft as any).reviewedAt = data.reviewedAt;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),

        unassignCase: builder.mutation<CaseReviewResponse, { projectId: string; caseId: string }>({
            query: ({ projectId, caseId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/unassign`,
                method: 'POST',
            }),
            async onQueryStarted({ projectId, caseId }, { dispatch, queryFulfilled }) {
                const now = new Date().toISOString();
                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'ready';
                            (draft as any).claimedBy = null;
                            (draft as any).claimedByName = null;
                            (draft as any).claimedAt = null;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'ready';
                            (draft as any).claimedBy = null;
                            (draft as any).claimedByName = null;
                            (draft as any).claimedAt = null;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = 'ready';
                                (draft[idx] as any).claimedBy = null;
                                (draft[idx] as any).claimedByName = null;
                                (draft[idx] as any).claimedAt = null;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                            (draft as any).claimedBy = data.claimedBy;
                            (draft as any).claimedByName = data.claimedByName;
                            (draft as any).claimedAt = data.claimedAt;
                            (draft as any).reviewedBy = data.reviewedBy;
                            (draft as any).reviewedByName = data.reviewedByName;
                            (draft as any).reviewedAt = data.reviewedAt;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),

        markCaseNotReportable: builder.mutation<
            CaseReviewResponse,
            { projectId: string; caseId: string; data: MarkNotReportableRequest }
        >({
            query: ({ projectId, caseId, data }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/not-reportable`,
                method: 'POST',
                body: data,
            }),
            async onQueryStarted({ projectId, caseId, data: body }, { dispatch, queryFulfilled }) {
                const now = new Date().toISOString();
                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'not-reportable';
                            (draft as any).feedback = body?.reason ?? (draft as any).feedback;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'not-reportable';
                            (draft as any).feedback = body?.reason ?? (draft as any).feedback;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = 'not-reportable';
                                (draft[idx] as any).feedback = body?.reason ?? (draft[idx] as any).feedback;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),

        submitCase: builder.mutation<
            CaseReviewResponse,
            { projectId: string; caseId: string; data: SubmitCaseRequest }
        >({
            query: ({ projectId, caseId, data }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/submit`,
                method: 'POST',
                body: data,
            }),
            async onQueryStarted({ projectId, caseId, data: body }, { dispatch, queryFulfilled }) {
                const now = new Date().toISOString();

                // Managers always act as reviewers — submit always goes to pending-review.
                const optimisticStatus = 'pending-review';

                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = optimisticStatus;
                            (draft as any).submittedAt = now;
                            if (body?.notes) (draft as any).feedback = body.notes;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = optimisticStatus;
                            if (body?.notes) (draft as any).feedback = body.notes;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = optimisticStatus;
                                (draft[idx] as any).submittedAt = now;
                                if (body?.notes) (draft[idx] as any).feedback = body.notes;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                            (draft as any).reviewedBy = data.reviewedBy;
                            (draft as any).reviewedByName = data.reviewedByName;
                            (draft as any).reviewedAt = data.reviewedAt;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),

        requestCaseChanges: builder.mutation<
            CaseReviewResponse,
            { projectId: string; caseId: string; data: RequestChangesRequest }
        >({
            query: ({ projectId, caseId, data }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/request-changes`,
                method: 'POST',
                body: data,
            }),
            async onQueryStarted({ projectId, caseId, data: body }, { dispatch, queryFulfilled, getState }) {
                const now = new Date().toISOString();
                const state: any = getState();
                const currentUserId: string | undefined = state?.user?.id ?? undefined;
                const currentUserName: string | undefined = state?.user?.name ?? undefined;

                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'in-progress';
                            (draft as any).reviewedBy = currentUserId ?? (draft as any).reviewedBy;
                            (draft as any).reviewedByName = currentUserName ?? (draft as any).reviewedByName;
                            (draft as any).reviewedAt = now;
                            (draft as any).feedback = body.feedback;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'in-progress';
                            (draft as any).reviewedBy = currentUserId ?? (draft as any).reviewedBy;
                            (draft as any).reviewedByName = currentUserName ?? (draft as any).reviewedByName;
                            (draft as any).reviewedAt = now;
                            (draft as any).feedback = body.feedback;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = 'in-progress';
                                (draft[idx] as any).reviewedBy = currentUserId ?? (draft[idx] as any).reviewedBy;
                                (draft[idx] as any).reviewedByName =
                                    currentUserName ?? (draft[idx] as any).reviewedByName;
                                (draft[idx] as any).reviewedAt = now;
                                (draft[idx] as any).feedback = body.feedback;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                            (draft as any).reviewedBy = data.reviewedBy;
                            (draft as any).reviewedByName = data.reviewedByName;
                            (draft as any).reviewedAt = data.reviewedAt;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),

        approveCase: builder.mutation<
            CaseReviewResponse,
            { projectId: string; caseId: string; data: ApproveCaseRequest }
        >({
            query: ({ projectId, caseId, data }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/approve`,
                method: 'POST',
                body: data,
            }),
            async onQueryStarted({ projectId, caseId, data: body }, { dispatch, queryFulfilled, getState }) {
                const now = new Date().toISOString();
                const state: any = getState();
                const currentUserId: string | undefined = state?.user?.id ?? undefined;
                const currentUserName: string | undefined = state?.user?.name ?? undefined;

                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'validated';
                            (draft as any).reviewedBy = currentUserId ?? (draft as any).reviewedBy;
                            (draft as any).reviewedByName = currentUserName ?? (draft as any).reviewedByName;
                            (draft as any).reviewedAt = now;
                            if (body?.notes) (draft as any).feedback = body.notes;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'validated';
                            (draft as any).reviewedBy = currentUserId ?? (draft as any).reviewedBy;
                            (draft as any).reviewedByName = currentUserName ?? (draft as any).reviewedByName;
                            (draft as any).reviewedAt = now;
                            if (body?.notes) (draft as any).feedback = body.notes;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = 'validated';
                                (draft[idx] as any).reviewedBy = currentUserId ?? (draft[idx] as any).reviewedBy;
                                (draft[idx] as any).reviewedByName =
                                    currentUserName ?? (draft[idx] as any).reviewedByName;
                                (draft[idx] as any).reviewedAt = now;
                                if (body?.notes) (draft[idx] as any).feedback = body.notes;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                            (draft as any).reviewedBy = data.reviewedBy;
                            (draft as any).reviewedByName = data.reviewedByName;
                            (draft as any).reviewedAt = data.reviewedAt;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),

        submitCaseToRegistry: builder.mutation<CaseReviewResponse, { projectId: string; caseId: string }>({
            query: ({ projectId, caseId }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/submit-to-registry`,
                method: 'POST',
            }),
            async onQueryStarted({ projectId, caseId }, { dispatch, queryFulfilled }) {
                const now = new Date().toISOString();
                const patches = [
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'submitted-to-registry';
                            (draft as any).submittedToRegistryAt = now;
                            (draft as any).updatedAt = now;
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = 'submitted-to-registry';
                        })
                    ),
                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) {
                                (draft[idx] as any).reviewStatus = 'submitted-to-registry';
                                (draft[idx] as any).submittedToRegistryAt = now;
                                (draft[idx] as any).updatedAt = now;
                            }
                        })
                    ),
                ];

                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseReview', { projectId, caseId }, draft => {
                            Object.assign(draft, data);
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getCaseStatus', { projectId, caseId }, draft => {
                            (draft as any).reviewStatus = data.reviewStatus;
                        })
                    );

                    dispatch(
                        projectsApi.util.updateQueryData('getProjectCaseReviews', projectId, draft => {
                            const idx = draft.findIndex(r => r.caseId === caseId);
                            if (idx >= 0) draft[idx] = data;
                        })
                    );
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
            invalidatesTags: (result, error, { projectId, caseId }) => [
                { type: 'CaseReview' as const, id: `${projectId}-${caseId}` },
                { type: 'CaseReview' as const, id: projectId },
            ],
        }),
        validateCaseFields: builder.mutation<
            ValidateCaseFieldsResponse,
            {
                projectId: string;
                caseId: string;
                data: { naaccrItems: { key: number; value: string }[]; metafileTag?: string; editsetTag?: string };
            }
        >({
            query: ({ projectId, caseId, data }) => ({
                url: `/patient-registry/projects/${projectId}/cases/${caseId}/validate`,
                method: 'POST',
                body: data,
            }),
        }),
    }),
});

export const {
    useGetProjectsQuery,
    useGetProjectCategoriesQuery,
    useGetProjectQuery,
    useGetProjectFieldDefinitionsQuery,
    useLazyGetProjectQuery,
    useGetCaseExtractionResultQuery,
    useGetCaseStatusQuery,
    useGetCaseDocumentsQuery,
    useGetCaseAuditTrailQuery,
    useGetCaseVersionsQuery,
    useCompareCaseVersionsQuery,
    useGetCaseVersionQuery,
    useActivateCaseVersionMutation,
    useResetCaseVersionsMutation,
    useSaveCaseVersionMutation,
    useGetProjectCasesQuery,
    useLazyGetProjectCasesQuery,
    useLazyCheckProjectTitleAvailabilityQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useDeleteProjectMutation,
    useGetCaseFindingStatusQuery,
    useRerunCaseFindingMutation,
    useStartCaseExtractionMutation,
    useGetReportingSourcesQuery,
    useCreateReportingSourceMutation,
    useDeleteReportingSourceMutation,
    useGetSelectionSummaryQuery,
    useLazyGetSelectionSummaryQuery,
    // Case Review hooks
    useGetCaseReviewQuery,
    useGetProjectCaseReviewsQuery,
    useClaimCaseMutation,
    useUnassignCaseMutation,
    useMarkCaseNotReportableMutation,
    useSubmitCaseMutation,
    useRequestCaseChangesMutation,
    useApproveCaseMutation,
    useSubmitCaseToRegistryMutation,
    useValidateCaseFieldsMutation,
} = projectsApi;
